import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { allInOneService } from '@/api/services/allInOneService'

export function useAllInOneBundles() {
  return useQuery({ queryKey: ['all-in-one', 'bundles'], queryFn: () => allInOneService.listBundles(), staleTime: 30_000 })
}

export function useSaveAllInOneBundle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => allInOneService.saveBundle(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['all-in-one', 'bundles'] }),
  })
}

export function useUpdateAllInOneBundle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number } & Record<string, unknown>) => allInOneService.updateBundle(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['all-in-one', 'bundles'] }),
  })
}

export function useDeleteAllInOneBundle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => allInOneService.deleteBundle(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['all-in-one', 'bundles'] }),
  })
}

export function useSaveAllInOneVariant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => allInOneService.saveVariant(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['all-in-one', 'bundles'] }),
  })
}

export function useDeleteAllInOneVariant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => allInOneService.deleteVariant(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['all-in-one', 'bundles'] }),
  })
}

export function useSaveAllInOneItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => allInOneService.saveItem(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['all-in-one', 'bundles'] }),
  })
}

export function useDeleteAllInOneItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => allInOneService.deleteItem(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['all-in-one', 'bundles'] }),
  })
}

export function useAllInOneEnrollments(p?: Parameters<typeof allInOneService.listEnrollments>[0], enabled = true) {
  return useQuery({ queryKey: ['all-in-one', 'enrollments', p], queryFn: () => allInOneService.listEnrollments(p), enabled, staleTime: 30_000, placeholderData: keepPreviousData })
}

export function useAllInOnePaidEnrollments(p: { user_id?: number } = {}, enabled = true) {
  return useQuery({ queryKey: ['all-in-one', 'enrollments', 'paid', p], queryFn: () => allInOneService.listPaidEnrollments(p), enabled, staleTime: 30_000 })
}

export function useAllInOneEnrollment(id?: number) {
  return useQuery({
    queryKey: ['all-in-one', 'enrollment', id],
    queryFn: () => allInOneService.getEnrollment(Number(id)),
    enabled: !!id,
  })
}

export function useAssignEnrollmentTrainer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; trainer_id: number; package_id: number; note?: string }) => allInOneService.assignTrainer(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['all-in-one', 'enrollment'] }),
  })
}

export function useCancelEnrollment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, note }: { id: number; note?: string }) => allInOneService.cancelEnrollment(id, { note }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['all-in-one', 'enrollments'] })
      qc.invalidateQueries({ queryKey: ['all-in-one', 'enrollment'] })
    },
  })
}

export function useMarkEnrollmentPaid() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; amount_paid?: number; reference?: string; note?: string }) => allInOneService.markPaid(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['all-in-one', 'enrollments'] })
      qc.invalidateQueries({ queryKey: ['all-in-one', 'enrollment'] })
    },
  })
}

export function useUpdateEnrollmentItemStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: {
      id: number; status: number; scheduled_at?: string | null; location?: string; result?: string; note?: string; payload?: Record<string, unknown>
    }) => allInOneService.updateEnrollmentItemStatus(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['all-in-one', 'enrollment'] }),
  })
}
